import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetAvailableDetailedExercises } from '../../../../redux/api/exercisesAPI';
import {
  useGetTrainingExercisesDetailedQuery,
  useGetTrainingsByPeriod,
} from '../../../../redux/api/trainingsAPI';
import { useGetRestrictionsPermissions } from '../../../../redux/api/users/usersApi';
import { TrainingPeriod } from '../../../../shared/constants';
import { IExercise } from '../../../../types/interfaces/exercise';
import { orderBy, uniqBy } from '../../../../utils/collections/array-utils';
import {
  ITrainingPeriodOption,
  filterTrainingPeriodOptionsByStatisticsAccess,
  getDefaultTrainingPeriodByStatisticsAccess,
} from '../../../../utils/statistics/filter-training-period-options-by-statistics-access';

export function useStatisticsOverview() {
  const { t } = useTranslation();
  const [selectedPeriod, setSelectedPeriod] = useState<TrainingPeriod>(
    TrainingPeriod.month,
  );

  const { data: restrictionPermissions, isLoading: isRestrictionsLoading } =
    useGetRestrictionsPermissions();

  const {
    data: periodTrainings = [],
    isLoading: isLoadingTrainings,
    isFetching: isFetchingTrainings,
  } = useGetTrainingsByPeriod(
    {
      period: selectedPeriod,
      finished: true,
    },
    { skip: !selectedPeriod },
  );

  const firstPeriodTrainingId = periodTrainings[0]?.id;

  const {
    data: availableDetailedExercises,
    isLoading: isLoadingAvailableDetailedExercises,
    isFetching: isFetchingAvailableDetailedExercises,
  } = useGetAvailableDetailedExercises();

  const { data: trainingExercisesDetailed } =
    useGetTrainingExercisesDetailedQuery(firstPeriodTrainingId, {
      skip: !firstPeriodTrainingId,
    });

  const exercises = useMemo(() => {
    if (
      isLoadingAvailableDetailedExercises ||
      isFetchingAvailableDetailedExercises
    ) {
      return undefined;
    }

    let result: IExercise[] = [];

    if (availableDetailedExercises) {
      result = [...availableDetailedExercises];
    }

    if (trainingExercisesDetailed) {
      result = [...result, ...trainingExercisesDetailed];
    }

    return result.length > 0
      ? orderBy(uniqBy(result, 'id'), 'name')
      : undefined;
  }, [
    availableDetailedExercises,
    trainingExercisesDetailed,
    isLoadingAvailableDetailedExercises,
    isFetchingAvailableDetailedExercises,
  ]);

  const allPeriodOptions: ITrainingPeriodOption[] = useMemo(
    () => [
      { id: TrainingPeriod.week, name: t('statistics.period_week') },
      { id: TrainingPeriod.month, name: t('statistics.period_month') },
      { id: TrainingPeriod.threeMonth, name: t('statistics.period_3month') },
      { id: TrainingPeriod.sixMonth, name: t('statistics.period_6month') },
      { id: TrainingPeriod.oneYear, name: t('statistics.period_1year') },
      { id: TrainingPeriod.allTime, name: t('statistics.period_allTime') },
    ],
    [t],
  );

  const periodOptions = useMemo(() => {
    if (isRestrictionsLoading || !restrictionPermissions) {
      return allPeriodOptions;
    }
    return filterTrainingPeriodOptionsByStatisticsAccess(
      allPeriodOptions,
      restrictionPermissions.periodStatisticsAccess,
    );
  }, [allPeriodOptions, isRestrictionsLoading, restrictionPermissions]);

  useEffect(() => {
    const defaultPeriod =
      getDefaultTrainingPeriodByStatisticsAccess(periodOptions);
    if (!defaultPeriod) {
      return;
    }
    if (!periodOptions.some((opt) => opt.id === selectedPeriod)) {
      setSelectedPeriod(defaultPeriod);
    }
  }, [periodOptions, selectedPeriod]);

  const selectedPeriodOption = useMemo(
    () => periodOptions.find((opt) => opt.id === selectedPeriod),
    [periodOptions, selectedPeriod],
  );

  const handlePeriodChange = (e: {
    target?: { value?: { id?: TrainingPeriod } };
  }) => {
    const value = e?.target?.value;
    if (value?.id) {
      setSelectedPeriod(value.id);
    }
  };

  return {
    selectedPeriod,
    selectedPeriodOption,
    periodOptions,
    periodTrainings,
    exercises,
    isLoadingTrainings,
    isFetchingTrainings,
    handlePeriodChange,
  };
}
